/**
 * Kiosk Settings Repository
 * Handles data persistence logic with delta calculation
 * Only sends changed fields to API
 */

import { KioskSettingsApi } from './kiosk-settings-api';
import {
  KioskSettings,
  KioskSettingsJson,
  UpdateKioskSettingsRequest
} from '../types/kiosk-settings-types';

export class KioskSettingsRepository {
  constructor(private readonly api: KioskSettingsApi) {}

  /**
   * Fetch kiosk settings
   */
  async getSettings(operatorId: number): Promise<KioskSettings> {
    return this.api.getKioskSettings(operatorId);
  }

  /**
   * Update settings with full current object
   * IMPORTANT: The Kiosk API replaces the entire settings, not merges.
   * We must send the full current settings object.
   * @param operatorId - The operator ID
   * @param countryCode - Country code from API response
   * @param partyId - Party ID from API response
   * @param parentId - Parent actor ID from API response; present for sub-operators
   * @param current - Current settings state (FULL object, not delta)
   * @param original - Original settings state (for change detection logging)
   */
  async updateSettings(
    operatorId: number,
    countryCode: string | undefined,
    partyId: string | undefined,
    parentId: number | null | undefined,
    current: KioskSettingsJson,
    original: KioskSettingsJson
  ): Promise<void> {
    // Calculate delta for logging/tracking only (not for API payload)
    const delta = this.calculateDelta(original, current);

    console.log('[KioskSettingsRepository] Update initiated:', {
      operatorId,
      originalFields: Object.keys(original).length,
      currentFields: Object.keys(current).length,
      changedFields: Object.keys(delta),
      note: 'Sending FULL settings object (API replaces entire object)'
    });

    // Send FULL current settings to API (not delta)
    // The API does NOT merge - it replaces the entire settings object
    await this.api.updateKioskSettings(operatorId, countryCode, partyId, parentId, current);
  }

  /**
   * Calculate delta between original and current state
   * Returns only fields that have changed
   * Removes null/empty values from delta
   */
  private calculateDelta(
    original: KioskSettingsJson,
    current: KioskSettingsJson
  ): UpdateKioskSettingsRequest {
    const delta: UpdateKioskSettingsRequest = {};

    // Check all current fields
    Object.entries(current).forEach(([key, value]) => {
      const originalValue = (original as any)[key];

      // Deep comparison for objects and arrays
      if (!this.deepEqual(originalValue, value)) {
        delta[key] = value;
      }
    });

    // Check for deleted fields (in original but not in current)
    Object.keys(original).forEach((key) => {
      if (!(key in current)) {
        delta[key] = null;
      }
    });

    // Remove null/empty/undefined values from delta
    Object.keys(delta).forEach((key) => {
      const value = delta[key];

      if (value === null || value === undefined) {
        // Keep null for deleted fields, but remove undefined
        if (value === undefined) {
          delete delta[key];
        }
        return;
      }

      if (typeof value === 'string' && value === '') {
        delete delta[key];
      }

      if (Array.isArray(value) && value.length === 0) {
        delete delta[key];
      }

      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
        delete delta[key];
      }
    });

    console.log('[KioskSettingsRepository] Delta keys:', Object.keys(delta));

    return delta;
  }

  /**
   * Deep equality check for comparing nested objects and arrays
   */
  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    if (a == null || b == null) return a === b;

    if (typeof a !== typeof b) return false;

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => this.deepEqual(item, b[index]));
    }

    // Handle objects
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      return keysA.every((key) => this.deepEqual(a[key], b[key]));
    }

    return false;
  }
}
